import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { Landmark, Loader2 } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DatePicker } from '@/components/ui/DatePicker'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { cn } from '@lib/utils/helpers'
import { useUserRole } from '@/hooks/useUserRole'
import {
  submitPaymentExecAuth,
  submitPaymentRefundNote,
  updateChargebackPaymentAdjustment,
  updatePaymentCheck,
} from '../services/reportsService'
import { resolvePaymentLogBackTarget } from '../utils/paymentLogBackToList'
import type {
  PaymentLogAchCheckPaymentFormProps,
  PaymentLogActionFormsProps,
} from '../types/components'
import { CHECK_ROLES, EXEC_ROLES, REFUND_REVIEW_ROLES } from '../constants/paymentLogRoles'
import styles from '../styles/paymentLogDetail.module.css'

/** Angular has no maxlength; keep a practical cap for ACH / check txn ids. */
export const CHECK_ACH_TXN_NUMBER_MAX_LENGTH = 50

const CARD_CLASS =
  'rounded-xl border border-slate-200 bg-white p-5 shadow-sm'
const HEADING_CLASS =
  'mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500'
const LABEL_CLASS = 'mb-1 block text-sm font-semibold text-slate-700'
const INPUT_CLASS =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1597ff] focus:outline-none focus:ring-2 focus:ring-[#1597ff]/20'
const BUTTON_CLASS =
  'inline-flex min-h-11 items-center justify-center rounded-xl bg-[rgb(21,151,255)] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60'

/** Angular reads `selectedCurrentUserRole.actions.<key>.action_email|action_notification`. */
function hasActionFlag(actions: unknown, key: string): boolean {
  if (!actions || typeof actions !== 'object') return false
  const entry = (actions as Record<string, unknown>)[key]
  if (!entry || typeof entry !== 'object') return false
  const flags = entry as Record<string, unknown>
  return Boolean(flags.action_email || flags.action_notification)
}

function toDateInputValue(value: unknown): string {
  if (value == null || value === '') return new Date().toISOString().slice(0, 10)
  const raw = String(value)
  const asNumber = Number(raw)
  const date = /^\d{10}$/.test(raw)
    ? new Date(asNumber * 1000)
    : new Date(raw.includes(' ') ? raw.replace(' ', 'T') : raw)
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function RadioYesNo({ registration }: { registration: UseFormRegisterReturn }) {
  return (
    <div className="flex items-center gap-5">
      {[
        { value: '1', label: 'Yes' },
        { value: '0', label: 'No' },
      ].map((option) => (
        <label key={option.value} className="inline-flex items-center gap-2 text-sm text-slate-800">
          <input type="radio" value={option.value} className="h-4 w-4" {...registration} />
          {option.label}
        </label>
      ))}
    </div>
  )
}

/** Roles + status gate matching Angular ACH/Check Payment Detail block. */
export function canShowPaymentCheckForm(roleName: string, paymentStatus: string): boolean {
  return CHECK_ROLES.includes(roleName) && paymentStatus === 'ACH_Check_Pending'
}

/** Inline card used next to Payment Detail on transaction pages. */
export function PaymentLogAchCheckPaymentForm({
  logData,
  onRefresh,
  className,
  readOnly = false,
}: PaymentLogAchCheckPaymentFormProps) {
  const [submitting, setSubmitting] = useState(false)
  const paymentType = String(logData?.payment_type ?? '')
  const paymentLogId = logData?.payment_log_id

  const checkForm = useForm<{
    payment_source: string
    check_number: string
    payment_date: string
  }>({
    defaultValues: {
      payment_source: '',
      check_number: String(logData?.auth_code ?? ''),
      payment_date: toDateInputValue(logData?.created_at),
    },
  })
  const checkValues = checkForm.watch()

  const submitCheck = checkForm.handleSubmit((values) => {
    if (readOnly || paymentLogId == null) return
    setSubmitting(true)
    void (async () => {
      try {
        const payload: Record<string, unknown> = {
          check_number: values.check_number.trim(),
          payment_date: values.payment_date,
        }
        if (logData?.is_external_payment) payload.payment_source = values.payment_source
        await updatePaymentCheck(paymentLogId, payload)
        showSuccess('Payment Status Updated Sucessfully!')
        onRefresh()
      } catch (error) {
        showApiError(error)
      } finally {
        setSubmitting(false)
      }
    })()
  })

  return (
    <section
      className={cn(styles.cardSectionFullHeight, className)}
      data-testid="payment-log-ach-check-form"
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon}>
          <Landmark className="h-5 w-5" aria-hidden />
        </div>
        <h3 className={styles.cardTitle}>ACH/Check Payment Detail</h3>
      </div>

      {paymentType.includes('DEFAULT') ? (
        <dl className="mb-4 space-y-1.5 text-sm text-slate-700">
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            <dt className="font-semibold text-slate-600">Liberty Authorizer:</dt>
            <dd className="font-medium text-slate-800">
              {logData?.liberty_authorization_approval === '1'
                ? 'Approved'
                : logData?.liberty_authorization_approval === '0'
                  ? 'Not Approved'
                  : '—'}
            </dd>
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            <dt className="font-semibold text-slate-600">Authorizer:</dt>
            <dd className="font-medium text-slate-800">
              {String(logData?.liberty_authorization_user_name ?? '').trim() || '—'}
            </dd>
          </div>
        </dl>
      ) : null}

      <form onSubmit={submitCheck} className="flex flex-1 flex-col gap-4" noValidate>
        {logData?.is_external_payment ? (
          <div>
            <label htmlFor="payment_source" className={styles.fieldLabel}>
              Payment Method:
            </label>
            <select
              id="payment_source"
              className={cn(styles.fieldInput, 'mt-1')}
              disabled={readOnly}
              {...checkForm.register('payment_source')}
            >
              <option value="">Select</option>
              <option value="CARDCONNECT">CardConnect</option>
              <option value="PAYPAL">Paypal</option>
              <option value="PAPER_CHECK">Paper Check</option>
            </select>
          </div>
        ) : null}

        <div>
          <label htmlFor="check_number" className={styles.fieldLabel}>
            Check / ACH TXN Number:
          </label>
          <input
            id="check_number"
            type="text"
            maxLength={CHECK_ACH_TXN_NUMBER_MAX_LENGTH}
            className={cn(styles.fieldInput, 'mt-1')}
            placeholder="Enter check or ACH transaction number"
            readOnly={readOnly}
            disabled={readOnly}
            {...checkForm.register('check_number', {
              required: true,
              maxLength: CHECK_ACH_TXN_NUMBER_MAX_LENGTH,
            })}
          />
        </div>

        <div>
          <label htmlFor="payment_date" className={styles.fieldLabel}>
            Transaction Date:
          </label>
          <div className="mt-1">
            <DatePicker
              id="payment_date"
              value={checkValues.payment_date}
              onChange={(value) =>
                checkForm.setValue('payment_date', value, { shouldValidate: true, shouldDirty: true })
              }
              allowFutureDates
              showHelperText={false}
              className={styles.fieldInput}
              placeholder="MM/DD/YYYY"
              dataTestId="payment-log-check-transaction-date"
              disabled={readOnly}
            />
          </div>
        </div>

        {readOnly ? null : (
          <div className="mt-auto pt-1">
            <button
              type="submit"
              disabled={
                submitting ||
                !String(checkValues.check_number ?? '').trim() ||
                !checkValues.payment_date
              }
              className={BUTTON_CLASS}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        )}
      </form>
    </section>
  )
}

export function PaymentLogActionForms({
  logData,
  onRefresh,
  hideCheckForm = false,
}: PaymentLogActionFormsProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const backToList = () => {
    const target = resolvePaymentLogBackTarget({
      hash: location.hash,
      applicantId: logData?.applicant_id,
      state: location.state,
    })
    if (target) navigate(target)
    else navigate(-1)
  }
  const { roleName, isLibertyRole, currentRole } = useUserRole()
  const [confirm, setConfirm] = useState<{ message: string; run: () => Promise<void> } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const paymentType = String(logData?.payment_type ?? '')
  const paymentStatus = String(logData?.payment_status ?? '')
  const paymentLogId = logData?.payment_log_id
  const paId = logData?.pa_id
  const actions = (currentRole as unknown as { actions?: unknown } | undefined)?.actions

  const showExecReview =
    (hasActionFlag(actions, 'refund_contract_approval') ||
      REFUND_REVIEW_ROLES.includes(roleName)) &&
    paymentStatus === 'AuthorizationPending' &&
    !paId

  const showPaymentApproval =
    EXEC_ROLES.includes(roleName) &&
    paymentStatus === 'ACH_Check_Pending' &&
    paymentType !== 'APPLICATION_FEE_REFUND' &&
    paymentType !== 'DEFAULT' &&
    !logData?.executive_user_first_name

  const showCheckForm = canShowPaymentCheckForm(roleName, paymentStatus) && !hideCheckForm

  const showInvoiceForm =
    (hasActionFlag(actions, 'invoice_approval') || isLibertyRole) &&
    paymentType === 'CONTRACT_INVOICE' &&
    Boolean(paId) &&
    (['AuthorizationPending', 'Rejected'].includes(paymentStatus) ||
      (['Pending', 'Completed'].includes(paymentStatus) && Boolean(logData?.underwriter_date)))

  const showNotesForm = Boolean(logData?.is_external_payment)

  const authForm = useForm<{ executive_auth: string; executive_notes: string }>({
    defaultValues: { executive_auth: '', executive_notes: '' },
  })
  const notesForm = useForm<{ notes: string }>({
    defaultValues: {
      notes: String(logData?.executive_notes ?? logData?.transaction_notes ?? ''),
    },
  })
  const invoiceForm = useForm<{
    executive_auth: string
    executive_notes: string
    underwriter_auth: string
    underwriter_notes: string
  }>({
    defaultValues: {
      executive_auth: String(logData?.executive_auth ?? ''),
      executive_notes: '',
      underwriter_auth: String(logData?.underwriter_auth ?? ''),
      underwriter_notes: '',
    },
  })

  const authValues = authForm.watch()
  const invoiceValues = invoiceForm.watch()

  const runGuarded = async (action: () => Promise<void>) => {
    setSubmitting(true)
    try {
      await action()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
      setConfirm(null)
    }
  }

  /** Angular `submitForm()` confirm wording. */
  const execConfirmMessage = (executiveAuth: string) => {
    if (executiveAuth === '0' && paymentType !== 'DEFAULT') {
      return 'Confirm that You want to Deny this Refund?'
    }
    if (executiveAuth === '1' && paymentType.includes('REFUND')) {
      return 'Confirm that you want to Approve the Refund now?'
    }
    if (paymentType === 'DEFAULT') {
      return 'Confirm that You want to Complete Reimbursement now?'
    }
    return 'Confirm that You want to Complete Payment now?'
  }

  const submitExecAuth = authForm.handleSubmit((values) => {
    if (paymentLogId == null) return
    setConfirm({
      message: execConfirmMessage(values.executive_auth),
      run: () =>
        runGuarded(async () => {
          await submitPaymentExecAuth(
            paymentLogId,
            { ...values },
            paymentType === 'DEFAULT' && paymentStatus === 'Completed',
          )
          showSuccess('Payment Status Updated Sucessfully!')
          backToList()
        }),
    })
  })

  const submitNotes = notesForm.handleSubmit((values) => {
    if (paymentLogId == null) return
    void runGuarded(async () => {
      const payload: Record<string, unknown> = {
        notes: values.notes,
        adjustment_type: 'external_payment',
      }
      if (logData?.certificate_paid_by) {
        payload.certificate_paid_by = logData.certificate_paid_by
        payload.surcharge_fee = logData.surcharge_fee
      }
      if (logData?.payment_method) payload.payment_method = logData.payment_method
      await submitPaymentRefundNote(paymentLogId, payload)
      showSuccess('Note updated Sucessfully!')
      onRefresh()
    })
  })

  const submitInvoice = invoiceForm.handleSubmit((values) => {
    if (paId == null) return
    const message =
      values.underwriter_auth === '0'
        ? `Confirm that You don't want to approve this Invoice?`
        : values.underwriter_auth === '1'
          ? 'Confirm that You want to approve this Invoice?'
          : 'Confirm that You want to update status?'
    setConfirm({
      message,
      run: () =>
        runGuarded(async () => {
          await updateChargebackPaymentAdjustment(paId, { ...values })
          showSuccess('Status Updated Sucessfully!')
          const applicantId = logData?.applicant_id
          if (applicantId != null) navigate(`/application/${applicantId}`)
          else navigate(-1)
        }),
    })
  })

  const execAuthDisabled =
    submitting ||
    !authValues.executive_auth ||
    (authValues.executive_auth === '0' && !authValues.executive_notes)

  const invoiceDisabled =
    submitting ||
    !invoiceValues.underwriter_auth ||
    (invoiceValues.underwriter_auth === '0' && !invoiceValues.underwriter_notes)

  const anyFormVisible =
    showExecReview || showPaymentApproval || showCheckForm || showInvoiceForm || showNotesForm

  const contextRows = useMemo(
    () =>
      [
        ['Reason', logData?.transaction_reason],
        ['Notes', logData?.transaction_notes],
        ['Special Note', logData?.special_note],
      ].filter(([, value]) => value != null && value !== ''),
    [logData?.transaction_reason, logData?.transaction_notes, logData?.special_note],
  )

  if (!anyFormVisible) return null

  return (
    <div className="grid gap-5 xl:grid-cols-2" data-testid="payment-log-action-forms">
      <ConfirmDialog
        isOpen={confirm != null}
        title="Confirm"
        message={confirm?.message ?? ''}
        primaryLabel="Continue"
        onPrimary={() => void confirm?.run()}
        secondaryLabel="Cancel"
        onSecondary={() => setConfirm(null)}
        onDismiss={() => setConfirm(null)}
      />

      {showExecReview || showPaymentApproval ? (
        <section className={CARD_CLASS}>
          <h2 className={HEADING_CLASS}>
            {showExecReview ? 'Executive Review' : 'Payment Approval'}
          </h2>
          {showExecReview && contextRows.length ? (
            <dl className="mb-4 space-y-1 text-sm text-slate-700">
              {contextRows.map(([label, value]) => (
                <div key={String(label)} className="flex gap-2">
                  <dt className="font-semibold">{String(label)}:</dt>
                  <dd className="break-words">{String(value)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <form onSubmit={submitExecAuth} className="space-y-4" noValidate>
            <div>
              <span className={LABEL_CLASS}>
                {showExecReview ? 'Executive Authorization:' : 'Payment Approval:'}
              </span>
              <RadioYesNo registration={authForm.register('executive_auth', { required: true })} />
            </div>
            <div>
              <label htmlFor="executive_notes" className={LABEL_CLASS}>
                Note(required for no):
              </label>
              <textarea
                id="executive_notes"
                rows={2}
                className={INPUT_CLASS}
                {...authForm.register('executive_notes')}
              />
            </div>
            <button type="submit" disabled={execAuthDisabled} className={BUTTON_CLASS}>
              Save
            </button>
          </form>
        </section>
      ) : null}

      {showCheckForm ? (
        <PaymentLogAchCheckPaymentForm logData={logData} onRefresh={onRefresh} />
      ) : null}

      {showInvoiceForm ? (
        <section className={CARD_CLASS}>
          <h2 className={HEADING_CLASS}>Authorization Needed</h2>
          <p className="mb-4 text-sm text-slate-700">
            Approver: {String(logData?.underwriter_first_name ?? '')}{' '}
            {String(logData?.underwriter_last_name ?? '')}
          </p>
          <form onSubmit={submitInvoice} className="space-y-4" noValidate>
            {!logData?.underwriter_date ? (
              <>
                <div>
                  <span className={LABEL_CLASS}>Do you approve this invoice?</span>
                  <RadioYesNo registration={invoiceForm.register('underwriter_auth')} />
                </div>
                <div>
                  <label htmlFor="underwriter_notes" className={LABEL_CLASS}>
                    Note(required for no):
                  </label>
                  <textarea
                    id="underwriter_notes"
                    rows={2}
                    className={INPUT_CLASS}
                    {...invoiceForm.register('underwriter_notes')}
                  />
                </div>
                {!logData?.underwriter_auth ? (
                  <button type="submit" disabled={invoiceDisabled} className={BUTTON_CLASS}>
                    Save
                  </button>
                ) : null}
              </>
            ) : (
              <dl className="space-y-1 text-sm text-slate-700">
                <div className="flex gap-2">
                  <dt className="font-semibold">Payment Status:</dt>
                  <dd>{String(logData?.payment_status ?? '')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-semibold">Notes:</dt>
                  <dd className="break-words">{String(logData?.underwriter_notes ?? '')}</dd>
                </div>
              </dl>
            )}
          </form>
        </section>
      ) : null}

      {showNotesForm ? (
        <section className={CARD_CLASS}>
          <h2 className={HEADING_CLASS}>Note</h2>
          <form onSubmit={submitNotes} className="space-y-4" noValidate>
            <div>
              <label htmlFor="external_notes" className={LABEL_CLASS}>
                Note:
              </label>
              <textarea
                id="external_notes"
                rows={2}
                className={INPUT_CLASS}
                {...notesForm.register('notes', { required: true })}
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !notesForm.watch('notes')}
              className={BUTTON_CLASS}
            >
              Save
            </button>
          </form>
        </section>
      ) : null}
    </div>
  )
}
